/**
 * Vue directives for authorization-based conditional rendering
 */

import type { Directive, DirectiveBinding } from 'vue'
import { authUtil } from '@/utils/authorization'
import type { UserRole } from '@/types'

interface AuthDirectiveValue {
    permission?: string | string[]
    role?: UserRole | UserRole[]
    action?: string
    resource?: any
    organization?: string
    fallback?: 'hide' | 'disable' | 'readonly'
}

/**
 * v-auth directive for permission-based conditional rendering
 * 
 * Usage examples:
 * v-auth="'view_products'"
 * v-auth="{ permission: 'edit_products' }"
 * v-auth="{ role: 'admin' }"
 * v-auth="{ permission: ['edit_products', 'delete_products'] }"
 * v-auth="{ role: ['admin', 'manager'] }"
 * v-auth="{ action: 'complete_tasks', resource: task }"
 * v-auth="{ permission: 'view_products', fallback: 'disable' }"
 */
export const vAuth: Directive<HTMLElement, string | AuthDirectiveValue> = {
    mounted(el: HTMLElement, binding: DirectiveBinding<string | AuthDirectiveValue>) {
        updateElementAuth(el, binding)
    },

    updated(el: HTMLElement, binding: DirectiveBinding<string | AuthDirectiveValue>) {
        updateElementAuth(el, binding)
    }
}

/**
 * v-role directive for role-based conditional rendering
 * 
 * Usage examples:
 * v-role="'admin'"
 * v-role="['admin', 'manager']"
 */
export const vRole: Directive<HTMLElement, UserRole | UserRole[]> = {
    mounted(el: HTMLElement, binding: DirectiveBinding<UserRole | UserRole[]>) {
        updateElementRole(el, binding)
    },

    updated(el: HTMLElement, binding: DirectiveBinding<UserRole | UserRole[]>) {
        updateElementRole(el, binding)
    }
}

/**
 * v-permission directive for permission-based conditional rendering
 * 
 * Usage examples:
 * v-permission="'view_products'"
 * v-permission="['edit_products', 'delete_products']"
 */
export const vPermission: Directive<HTMLElement, string | string[]> = {
    mounted(el: HTMLElement, binding: DirectiveBinding<string | string[]>) {
        updateElementPermission(el, binding)
    },

    updated(el: HTMLElement, binding: DirectiveBinding<string | string[]>) {
        updateElementPermission(el, binding)
    }
}

/**
 * Update element based on auth directive
 */
function updateElementAuth(el: HTMLElement, binding: DirectiveBinding<string | AuthDirectiveValue>) {
    const value = binding.value
    let hasAccess = true
    let fallback: 'hide' | 'disable' | 'readonly' = 'hide'

    if (typeof value === 'string') {
        // Simple permission string
        hasAccess = authUtil.hasPermission(value)
    } else if (typeof value === 'object' && value !== null) {
        // Complex auth object
        fallback = value.fallback || 'hide'

        // Check permission
        if (value.permission) {
            if (Array.isArray(value.permission)) {
                hasAccess = authUtil.hasAllPermissions(value.permission)
            } else {
                hasAccess = authUtil.hasPermission(value.permission)
            }
        }

        // Check role
        if (hasAccess && value.role) {
            if (Array.isArray(value.role)) {
                hasAccess = authUtil.hasAnyRole(value.role)
            } else {
                hasAccess = authUtil.hasRole(value.role)
            }
        }

        // Check action with resource
        if (hasAccess && value.action) {
            hasAccess = authUtil.canPerformAction(value.action, value.resource)
        }

        // Check organization access
        if (hasAccess && value.organization) {
            hasAccess = authUtil.canAccessOrganization(value.organization)
        }
    }

    applyAuthResult(el, hasAccess, fallback)
}

/**
 * Update element based on role directive
 */
function updateElementRole(el: HTMLElement, binding: DirectiveBinding<UserRole | UserRole[]>) {
    const roles = binding.value
    let hasAccess = false

    if (Array.isArray(roles)) {
        hasAccess = authUtil.hasAnyRole(roles)
    } else {
        hasAccess = authUtil.hasRole(roles)
    }

    applyAuthResult(el, hasAccess, 'hide')
}

/**
 * Update element based on permission directive
 */
function updateElementPermission(el: HTMLElement, binding: DirectiveBinding<string | string[]>) {
    const permissions = binding.value
    let hasAccess = false

    if (Array.isArray(permissions)) {
        hasAccess = authUtil.hasAllPermissions(permissions)
    } else {
        hasAccess = authUtil.hasPermission(permissions)
    }

    applyAuthResult(el, hasAccess, 'hide')
}

/**
 * Apply authorization result to element
 */
function applyAuthResult(el: HTMLElement, hasAccess: boolean, fallback: 'hide' | 'disable' | 'readonly') {
    // Store original state if not already stored
    if (!el.dataset.authOriginalDisplay) {
        el.dataset.authOriginalDisplay = el.style.display || ''
    }
    if (!el.dataset.authOriginalDisabled) {
        el.dataset.authOriginalDisabled = (el as any).disabled ? 'true' : 'false'
    }
    if (!el.dataset.authOriginalReadonly) {
        el.dataset.authOriginalReadonly = (el as any).readOnly ? 'true' : 'false'
    }

    if (hasAccess) {
        // Restore original state
        el.style.display = el.dataset.authOriginalDisplay || ''

        if (el.dataset.authOriginalDisabled === 'false') {
            (el as any).disabled = false
        }

        if (el.dataset.authOriginalReadonly === 'false') {
            (el as any).readOnly = false
        }

        // Remove auth classes
        el.classList.remove('auth-hidden', 'auth-disabled', 'auth-readonly')
    } else {
        // Apply fallback behavior
        switch (fallback) {
            case 'hide':
                el.style.display = 'none'
                el.classList.add('auth-hidden')
                break

            case 'disable':
                (el as any).disabled = true
                el.classList.add('auth-disabled')
                break

            case 'readonly':
                (el as any).readOnly = true
                el.classList.add('auth-readonly')
                break
        }
    }
}

/**
 * Register all auth directives with a Vue app
 */
export function registerAuthDirectives(app: any) {
    app.directive('auth', vAuth)
    app.directive('role', vRole)
    app.directive('permission', vPermission)
}

// Export individual directives
export default {
    auth: vAuth,
    role: vRole,
    permission: vPermission
}